import { SelectOptionModel } from 'traveldesk-ui'
import BaseEntity from '../../models/BaseEntity'
import { BookingType } from '../../models/OfferBookingType'
import { OfferPrivateType } from '../../models/OfferPrivateType'
import { ExternalCategoryMappingModel, ExternalCityMappingModel } from '../../services/ExternalIntegrationsApiService'

/** Matches server enum CustomInputType (BookingCustomInput.cs) */
export enum CustomInputType {
    Unknown,
    Text,
    Number,
    Select,
    Date,
    MultiSelect,
    Boolean,
    File,
    Image,
    Address,
    Time,
    DateTime,
    Country,
    Phone,
    FlightNumber,
    Hotel
}

export enum CustomInputPer {
    PerBooking = 0,
    PerPax = 1,
}

export const CustomInputPerOptions = [
    { value: CustomInputPer.PerBooking, label: 'Per Booking' },
    { value: CustomInputPer.PerPax, label: 'Per Pax' },
]

export interface CustomFieldImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    fieldName: string
    value: string
}

export interface CustomFieldImportModel {
    fieldName: string
    value: string
    translations?: CustomFieldImportTranslationModel[]
}
export class TourImportCacheModel {
    apiProviderId: number = 0
    cacheDateTime?: moment.Moment
    tourImportModel?: TourImportModel
    mappedTourId?: number
    externalId: string = ''
    fileName: string = ''
    isLoading: boolean = false
    tourName: string = ''
}
export interface TourImportCacheFileModel{
    apiProviderId:number
    externalId:string
    fileName: string
}
export class TourImportModel extends BaseEntity {
    public static Create(object: TourImportModel){
        return Object.assign(new TourImportModel(), object, {
            offers: object.offers?.map(offer => OfferImportModel.Create(offer)),
        });
    }
    updateOriginsMeta(externalCitiesMappings: ExternalCityMappingModel[]){
        this.offers?.forEach(offer => {
            offer.updateOriginsMeta(externalCitiesMappings)
        })
    }
    updateToursCategories(externalCategoriesMappings: ExternalCategoryMappingModel[]) {
        if (!this.externalCategoryIds) {
            return
        }

        this.toursCategories = Array.from(new Set(
            this.externalCategoryIds
                .map(externalCategoryId => externalCategoriesMappings.find(mapping => mapping.apiIntegrationCategoryId === externalCategoryId)?.ourCategoryId)
                .filter((categoryId): categoryId is number => categoryId != null),
        ))
    }
    /** Any external Id (number, uuid etc) */
    externalId?: string
    /** Source last updated date (ISO string from API) */
    lastEditDate?: string
    /** Tour name */
    name?: string
    description?: string
    seoDescription?: string
    termsAndConditions?: string
    /** Map all unknown fields to custom fields */
    customFields?: CustomFieldImportModel[]
    tourItinerary?: string
    locationTiming?: string
    locationAddress?: string
    locationLatitude?: number
    locationLongitude?: number
    externalCurrencyId?: string
    baseCurrencyCode?: string
    customReviewCount?: number
    customReviewsRating?: number
    toursCategories?: number[]
    photos?: string[]
    translations?: TourImportTranslationModel[]
    translationLanguages?: string[]
    attachToPricingPresetsIds?: number[]
    offers?: OfferImportModel[]
    cityId?: number
    cityName: string = ''
    externalCityId?: string    
    externalCountryId?: string    
    externalCategoryIds?: string[]
    cancellationPolicy?: CancellationPolicyImportModel[]
    isPublished: boolean = false
    get rowSpan() {        
        return this.offers?.reduce((res, offer) => {
            return res + offer.rowSpan
        }, 0) ?? 1
    }
    get rowSpanCollapse(){
        let offers = (this.offers && (this.offers?.length??0>3)) ? this.offers.slice(0, 3) : this.offers;
        return offers?.reduce((res, offer) => {
            return res + offer.rowSpan
        }, 0) ?? 1
    }
    get firstOfferName(){
        return this.offers?.[0]?.nameDisplay || this.offers?.[0]?.externalId || '';
    }
    get firstOfferRowSpan(){
        return 1
        return Math.max(3,this.offers?.[0]?.pricingPeriods?.length??1);
    }
    get firstOffer() {
        return this?.offers?.[0] ?? undefined;
    }    
}
export interface ExternalTourSelectOptionModel extends SelectOptionModel{    
    mappedTourId: number    
}

export interface TourImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    /** Translated name */
    nameDisplay?: string
    description?: string
    seoDescription?: string
    termsAndConditions?: string
    tourItinerary?: string
}
export class OfferImportOriginMeta {
    cityId?: number = 0
    cityName: string = ''
    externalCityId: string = ''
}

export class OfferImportModel {
    public static Create(object: OfferImportModel){
        return Object.assign(new OfferImportModel(), object);
    }
    /** Any external Id (number, uuid etc) */
    updateOriginsMeta(externalCitiesMappings: ExternalCityMappingModel[]){
        this.externalOriginsMeta = this.externalOriginsMeta?.filter(x=>x.cityId) || [] //keep only mapped cities
        this.externalOriginsMeta.forEach(meta => {
            if(!meta.externalCityId){
                const externalCityMapping = externalCitiesMappings.find(x=>String(x.ourCityId) === String(meta.cityId))
                if(externalCityMapping){
                    meta.externalCityId = externalCityMapping.apiIntegrationCityId
                }
            }
        })
        this.externalOriginIds?.forEach(x=>{
            const externalCityMapping = externalCitiesMappings.find(z=>String(z.apiIntegrationCityId) === String(x))
            if(externalCityMapping)
            {
                if(externalCityMapping.ourCityId && this.originIds?.some(originId => String(originId) === String(externalCityMapping.ourCityId)))
                {
                
                }
                else {
                    this.externalOriginsMeta?.push(Object.assign(new OfferImportOriginMeta(), { 
                        cityId: externalCityMapping.ourCityId,
                        cityName: externalCityMapping.apiIntegrationCityName,
                        externalCityId: x
                    }))
                }
            }
        })
    }
    externalId?: string
    nameDisplay?: string
    description?: string
    minNumOfPeople?: number
    maxNumOfPeople?: number
    isDirectReporting?: boolean
    customFields?: CustomFieldImportModel[]
    customInputs?: CustomInputImportModel[]
    originIds?: number[]
    externalOriginIds?: string[]
    externalOriginsMeta?: OfferImportOriginMeta[]
    translations?: OfferImportTranslationModel[]
    privateType?: OfferPrivateType
    bookingType?: BookingType
    notesToClientsPostBooking?: string
    pickUpMeetingPointInformation?: string
    numberOfHours?: number
    numberOfMinutes?: number
    minimumAdvancedBooking?: number
    childrenAllowed?: boolean
    infantsAllowed?: boolean
    childAge?: number
    infantAge?: number
    utcTimeShift?: number
    timeZone?: string
    timeSlots?: TimeSlotImportModel[]
    ticketTypes?: TicketTypeImportModel[]
    pricingPeriods?: OfferPricingPeriodImportModel[]
    cancellationPolicy?: CancellationPolicyImportModel[]
    isPublished: boolean = false
    get rowSpan() {
        return 1
    }
}
export interface TicketTypeImportModel {
    id: string,
    type: string,
    label: string,
    description: string,
    allowed: boolean
}
export interface OfferImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    nameDisplay?: string
    description?: string
}

export interface TimeSlotImportModel {
    externalId?: string
    startTime?: number
    endTime?: number
}

export interface OfferPricingPeriodImportModel {
    periodStart?: string
    periodEnd?: string | null
    recPricePerAdultBaseCurrency?: number
    recPricePerChildBaseCurrency?: number
    recPricePerTripBaseCurrency?: number
    netPricePerAdultBaseCurrency?: number
    netPricePerChildBaseCurrency?: number
    netPricePerTripBaseCurrency?: number
    retailPricePerAdultBaseCurrency?: number
    retailPricePerChildBaseCurrency?: number
    retailPricePerTripBaseCurrency?: number
    retailPricePerAdultSellingCurrency?: number
    retailPricePerChildSellingCurrency?: number
    retailPricePerTripSellingCurrency?: number
    minimumSellingPricePerAdultBaseCurrency?: number
    minimumSellingPricePerChildBaseCurrency?: number
    minimumSellingPricePerTripBaseCurrency?: number
    minimumSellingPricePerAdultSellingCurrency?: number
    minimumSellingPricePerChildSellingCurrency?: number
    minimumSellingPricePerTripSellingCurrency?: number
}

export interface CustomInputImportModel {
    externalId?: string
    name?: string
    description?: string
    translations?: CustomInputTranslationModel[]
    isRequired?: boolean
    isAddon?: boolean
    regexValidation?: string
    type?: CustomInputType
    per?: CustomInputPer
    isAutocomplete: boolean
    isPaginated: boolean
    /** External API uses bookingScope; mapped to `per` when available */
    bookingScope?: CustomInputPer
    minNumber?: number
    maxNumber?: number
    validFrom?: string
    validTo?: string
    price?: number
    items?: CustomInputSelectOptionModel[]
}

export interface CustomInputTranslationModel {
    /** Translation ISO code */
    languageCode: string
    name?: string
    description?: string
}

export interface CustomInputSelectOptionModel {
    label?: string
    translations?: CustomInputSelectOptionTranslationModel[]
    value?: string
    price?: number
    children?: CustomInputImportModel[]
}

export interface CustomInputSelectOptionTranslationModel {
    /** Translation ISO code */
    languageCode: string
    label?: string
}

export interface CancellationPolicyImportModel {
    percents?: number
    hours?: number
}
